/**
 * @file 学员管理页 · 类型与 mock
 * @description 学员基础信息 + 按科目维度的 details；选项对齐 `@/enum/common`。
 *
 * @whenToUse 仅学员管理页。
 * @whenNotToUse 其他业务模块。
 */

import {
  EducationStage,
  Grade,
  LearningLevel,
  Semester,
  Subject,
  educationStageMap,
  gradeMap,
  learningLevelMap,
  semesterMap,
  subjectMap,
} from '@/enum/common.ts'

/** 学员状态 */
export type StudentStatus = 'normal' | 'trial' | 'disabled'

/** 教材（业务 mock，暂未进公共枚举） */
export type Textbook = 'renjiao' | 'tongyong' | 'huashida'

/** 按科目维度的学习详情 */
export type StudentDetail = {
  /** 详情 ID */
  id: string
  /** 科目 */
  subject: Subject | string
  /** 教材 */
  textbook: Textbook | string
  /** 年级 */
  grade: Grade | string
  /** 学期 */
  semester: Semester | string
  /** 教育阶段 */
  stage: EducationStage | string
  /** 学习力等级 */
  learningLevel: LearningLevel | string
  /** 学习力数值 */
  learningPower: number
  /** 所属教师名称；新建时可空 */
  teacherName: string
}

/** 学员行 */
export type StudentRow = {
  /** 学员 ID */
  id: string
  /** 学员名称 */
  name: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 状态 */
  status: StudentStatus
  /** 创建时间 */
  createdAt: string
  /** 科目维度详情（至少一条） */
  details: StudentDetail[]
  /** 是否允许 PAD 端调整基本信息；默认否，仅主账号可改 */
  allowEditBasicInfo: boolean
}

/** 表单内单条科目详情 */
export type StudentDetailFormValues = {
  /** 本地键，用于列表渲染 */
  key: string
  /** 科目 */
  subject: string
  /** 教材 */
  textbook: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  stage: string
  /** 学习力等级 */
  learningLevel: string
  /** 学习力 */
  learningPower: number
  /** 所属教师名称 */
  teacherName: string
}

/** 新建 / 编辑表单 */
export type StudentFormValues = {
  /** 学员名称 */
  name: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 科目详情集合（至少一条） */
  details: StudentDetailFormValues[]
}

/** 状态下拉 / Badge 文案 */
export const STUDENT_STATUS_LABEL: Record<StudentStatus, string> = {
  normal: '正常',
  trial: '试学',
  disabled: '禁用',
}

/** 教材映射 */
export const textbookMap: Record<Textbook, string> = {
  renjiao: '人教版',
  tongyong: '通用版',
  huashida: '华师大版',
}

/**
 * 将枚举映射转为 Select 选项
 * @param map 枚举文案表
 */
export function mapToOptions(
  map: Record<string, string>,
): { value: string; label: string }[] {
  return Object.entries(map).map(([value, label]) => ({ value, label }))
}

export const TEXTBOOK_OPTIONS = mapToOptions(textbookMap)
export const SUBJECT_OPTIONS = mapToOptions(subjectMap)
export const GRADE_OPTIONS = mapToOptions(gradeMap)
export const SEMESTER_OPTIONS = mapToOptions(semesterMap)
export const STAGE_OPTIONS = mapToOptions(educationStageMap)
export const LEARNING_LEVEL_OPTIONS = mapToOptions(learningLevelMap)

/** 可选教师（本地 mock） */
export type MockTeacher = {
  /** 教师 ID */
  id: string
  /** 教师姓名 */
  name: string
}

export const MOCK_TEACHERS: MockTeacher[] = [
  { id: 't-1', name: '王老师' },
  { id: 't-2', name: '刘老师' },
  { id: 't-3', name: '李老师' },
  { id: 't-4', name: '张老师' },
  { id: 't-5', name: '陈老师' },
]

/** 教师下拉选项（value 为姓名，与详情 teacherName 对齐） */
export const TEACHER_OPTIONS = MOCK_TEACHERS.map((item) => ({
  value: item.name,
  label: item.name,
}))

/**
 * 创建空的科目详情表单项
 * @param key 本地键
 */
export function createEmptyDetailForm(
  key = `d-${Date.now()}`,
): StudentDetailFormValues {
  return {
    key,
    subject: '',
    textbook: '',
    grade: '',
    semester: '',
    stage: '',
    learningLevel: '',
    learningPower: 0,
    teacherName: '',
  }
}

export const EMPTY_STUDENT_FORM: StudentFormValues = {
  name: '',
  phone: '',
  points: 0,
  details: [createEmptyDetailForm('d-init')],
}

/** 选项 value → label */
export function optionLabel(
  options: readonly { value: string; label: string }[],
  value: string,
): string {
  return options.find((item) => item.value === value)?.label ?? (value || '—')
}

/** 初始 mock 学员 */
export const MOCK_STUDENTS: StudentRow[] = [
  {
    id: 'stu-1',
    name: '陈思远',
    phone: '13800138001',
    points: 320,
    status: 'normal',
    createdAt: '2026-03-12 09:20:00',
    allowEditBasicInfo: false,
    details: [
      {
        id: 'stu-1-d1',
        subject: Subject.MATH,
        textbook: 'renjiao',
        grade: Grade.EIGHT,
        semester: Semester.FIRST,
        stage: EducationStage.JUNIOR_HIGH,
        learningLevel: LearningLevel.B,
        learningPower: 76,
        teacherName: '王老师',
      },
      {
        id: 'stu-1-d2',
        subject: Subject.PHYSICS,
        textbook: 'huashida',
        grade: Grade.EIGHT,
        semester: Semester.SECOND,
        stage: EducationStage.JUNIOR_HIGH,
        learningLevel: LearningLevel.C,
        learningPower: 64,
        teacherName: '刘老师',
      },
    ],
  },
  {
    id: 'stu-2',
    name: '林雨桐',
    phone: '13900139002',
    points: 80,
    status: 'trial',
    createdAt: '2026-06-01 14:05:00',
    allowEditBasicInfo: false,
    details: [
      {
        id: 'stu-2-d1',
        subject: Subject.CHEMISTRY,
        textbook: 'tongyong',
        grade: Grade.HIGH_ONE,
        semester: Semester.FIRST,
        stage: EducationStage.SENIOR_HIGH,
        learningLevel: LearningLevel.C,
        learningPower: 58,
        teacherName: '',
      },
    ],
  },
  {
    id: 'stu-3',
    name: '赵一诺',
    phone: '13700137003',
    points: 0,
    status: 'disabled',
    createdAt: '2025-11-20 11:40:00',
    allowEditBasicInfo: true,
    details: [
      {
        id: 'stu-3-d1',
        subject: Subject.MATH,
        textbook: 'renjiao',
        grade: Grade.NINE,
        semester: Semester.SECOND,
        stage: EducationStage.JUNIOR_HIGH,
        learningLevel: LearningLevel.A,
        learningPower: 88,
        teacherName: '李老师',
      },
    ],
  },
]

/** 列表展平行（一科一行） */
export type StudentListRow = {
  /** 行 ID（详情 ID） */
  id: string
  /** 所属学员 ID */
  studentId: string
  /** 学员名称 */
  name: string
  /** 手机号 */
  phone: string
  /** 点数 */
  points: number
  /** 状态 */
  status: StudentStatus
  /** 创建时间 */
  createdAt: string
  /** 科目 */
  subject: string
  /** 教材 */
  textbook: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  stage: string
  /** 学习力等级 */
  learningLevel: string
  /** 学习力 */
  learningPower: number
  /** 所属教师 */
  teacherName: string
  /** 是否允许调整基本信息 */
  allowEditBasicInfo: boolean
}

/** 列表筛选条件 */
export type StudentQuery = {
  /** 学员名称（模糊） */
  name: string
  /** 状态；空为全部 */
  status: string
  /** 创建日期 `YYYY-MM-DD`；空为全部 */
  createdAt: string
  /** 科目 */
  subject: string
  /** 教材 */
  textbook: string
  /** 年级 */
  grade: string
  /** 学期 */
  semester: string
  /** 教育阶段 */
  stage: string
  /** 学习力等级 */
  learningLevel: string
  /** 学习力；undefined 表示不限 */
  learningPower: number | undefined
}

export const EMPTY_STUDENT_QUERY: StudentQuery = {
  name: '',
  status: '',
  createdAt: '',
  subject: '',
  textbook: '',
  grade: '',
  semester: '',
  stage: '',
  learningLevel: '',
  learningPower: undefined,
}

/**
 * 将学员展开为列表行（一科一行）
 * @param students 学员集合
 */
export function flattenStudentRows(students: StudentRow[]): StudentListRow[] {
  return students.flatMap((student) => {
    if (student.details.length === 0) {
      return [
        {
          id: `${student.id}-empty`,
          studentId: student.id,
          name: student.name,
          phone: student.phone,
          points: student.points,
          status: student.status,
          createdAt: student.createdAt,
          subject: '',
          textbook: '',
          grade: '',
          semester: '',
          stage: '',
          learningLevel: '',
          learningPower: 0,
          teacherName: '',
          allowEditBasicInfo: student.allowEditBasicInfo,
        },
      ]
    }
    return student.details.map((detail) => ({
      id: detail.id,
      studentId: student.id,
      name: student.name,
      phone: student.phone,
      points: student.points,
      status: student.status,
      createdAt: student.createdAt,
      subject: detail.subject,
      textbook: detail.textbook,
      grade: detail.grade,
      semester: detail.semester,
      stage: detail.stage,
      learningLevel: detail.learningLevel,
      learningPower: detail.learningPower,
      teacherName: detail.teacherName,
      allowEditBasicInfo: student.allowEditBasicInfo,
    }))
  })
}

/**
 * 筛选展平后的学员列表行
 * @param rows 展平行
 * @param query 筛选条件
 */
export function filterStudentListRows(
  rows: StudentListRow[],
  query: StudentQuery,
): StudentListRow[] {
  const name = query.name.trim()
  return rows.filter((row) => {
    if (name && !row.name.includes(name)) return false
    if (query.status && row.status !== query.status) return false
    if (query.createdAt && !row.createdAt.startsWith(query.createdAt)) {
      return false
    }
    if (query.subject && row.subject !== query.subject) return false
    if (query.textbook && row.textbook !== query.textbook) return false
    if (query.grade && row.grade !== query.grade) return false
    if (query.semester && row.semester !== query.semester) return false
    if (query.stage && row.stage !== query.stage) return false
    if (query.learningLevel && row.learningLevel !== query.learningLevel) {
      return false
    }
    if (
      query.learningPower !== undefined &&
      row.learningPower !== query.learningPower
    ) {
      return false
    }
    return true
  })
}
